import {
  BatchJobStatus,
  ExportJobFragment,
  GateUsageScheduleJobAssignmentFragment,
  GateUsageScheduleResourceFragment,
  SortDirection,
  useRunExportJobMutation,
} from '@warebee/frontend/data-access-api-graphql';
import {
  DatasetQueryConverterConfig,
  generateExportFilename,
  getDatasetQueryDataRows,
} from '@warebee/shared/export-converter';
import classNames from 'classnames';
import { format, startOfHour } from 'date-fns';
import _ from 'lodash';
import React, { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useRecoilValue } from 'recoil';
import { DatasetObjectFieldSummary } from '../../components/DatasetFieldsSummary';
import DatasetTable from '../../components/DatasetTable';
import * as Icon from '../../components/icons';
import TitleSection from '../../components/layout/TitleSection';
import { brandName } from '../../store/global.state';
import {
  warehouseSelected,
  warehouseSelectedId,
} from '../../store/warehouse.state';
import { analyzeGateUsageSchedule } from '../store/analyze.state';
import { simulationCurrent } from '../store/simulation.state';
import DockUsageChart from './DockUsageChart';
import useSimulationJobTimelineTableConfig from './useSimulationJobTimelineTableConfig';

export type JobTimelineDataTableRow = GateUsageScheduleJobAssignmentFragment & {
  resourceId: string;
};

export type SimulationJobTimelineDataTableProps = {
  hiddenDocks: Set<string>;
  toggleDockVisibility: (dock: string) => void;
  allFieldsSummary: DatasetObjectFieldSummary[];
  rawData: JobTimelineDataTableRow[];
};

function getRows(
  resources: GateUsageScheduleResourceFragment[],
): JobTimelineDataTableRow[] {
  return _(resources)
    .map(res => _.map(res.assignments, a => ({ ...a, resourceId: res.id })))
    .flatten()
    .value();
}

const SimulationJobTimelineDataTable: React.FC<
  SimulationJobTimelineDataTableProps
> = ({ hiddenDocks, toggleDockVisibility, allFieldsSummary, rawData }) => {
  const { t } = useTranslation('simulation');
  const schedule = useRecoilValue(analyzeGateUsageSchedule);
  const columnsConfig = useSimulationJobTimelineTableConfig();
  const [runExportCSV] = useRunExportJobMutation();

  // State management for search and sort
  const [tableState, setTableState] = useState<{
    searchValues: Record<string, string>;
    sortValues: Partial<Record<string, SortDirection>>;
  }>({
    searchValues: {},
    sortValues: { startTime: SortDirection.ASC }, // Default sort by start time
  });

  // Get context for export filename
  const sim = useRecoilValue(simulationCurrent);
  const brandTitleState = useRecoilValue(brandName);
  const warehouse = useRecoilValue(warehouseSelected);
  const warehouseIdState = useRecoilValue(warehouseSelectedId);

  // Filter the data based on search values
  const filteredData = useMemo(() => {
    if (_.isEmpty(tableState.searchValues)) {
      return rawData;
    }

    return rawData.filter(row => {
      return _.every(tableState.searchValues, (value, key) => {
        if (!value) return true;
        const rowValue = _.get(row, key)?.toString()?.toLowerCase();
        return rowValue?.includes(value.toLowerCase());
      });
    });
  }, [rawData, tableState.searchValues]);

  // Sort the filtered data
  const sortedData = useMemo(() => {
    if (_.isEmpty(tableState.sortValues)) {
      return filteredData;
    }

    return _.orderBy(
      filteredData,
      Object.entries(tableState.sortValues).map(([key, direction]) => {
        return (row: JobTimelineDataTableRow) => {
          const value = _.get(row, key);
          // Handle date strings by converting to timestamps for proper sorting
          if (key === 'startTime' || key === 'job.deadline') {
            return new Date(value).getTime();
          }
          // Handle numeric values
          if (typeof value === 'number') {
            return value;
          }
          // Handle string values
          return value?.toString()?.toLowerCase() || '';
        };
      }),
      Object.values(tableState.sortValues).map(direction =>
        direction === SortDirection.DESC ? 'desc' : 'asc',
      ) as ('desc' | 'asc')[],
    );
  }, [filteredData, tableState.sortValues]);

  const totalCount = _.size(sortedData);

  // Create converter config for export
  const converterConfig: DatasetQueryConverterConfig<JobTimelineDataTableRow> =
    {
      columnsConfig,
    };

  // Exclude dock stats from what's passed to the main table summary
  const tableFieldsSummary = useMemo(() => {
    const summaries = allFieldsSummary.filter(f => f.name !== 'resourceId');
    return _.orderBy(
      summaries,
      ['distinctValueCount', 'name'],
      ['desc', 'asc'],
    );
  }, [allFieldsSummary]);

  const hourlyStats = useMemo(() => {
    if (!rawData?.length) return [];

    const groupedByHour = _.groupBy(rawData, row => {
      // Group by a formatted hour string
      return format(startOfHour(new Date(row.startTime)), 'MMM dd, HH:00');
    });

    const calculatedStats = _.mapValues(groupedByHour, jobsInHour => ({
      totalJobs: jobsInHour.length,
      byDock: _.countBy(jobsInHour, 'resourceId'),
    }));

    // Sort by hour
    const sortedHours = _.sortBy(Object.keys(calculatedStats), hour =>
      new Date(hour).getTime(),
    );

    // Return a sorted array of [hour, stats]
    return sortedHours.map(hour => [hour, calculatedStats[hour]]);
  }, [rawData]);

  const chartData = useMemo(() => {
    if (!hourlyStats.length) return { data: [], docks: [] };

    const dockSet = new Set<string>();
    hourlyStats.forEach(([, stats]: [string, any]) => {
      Object.keys(stats.byDock).forEach(dock => dockSet.add(dock));
    });
    const docks = Array.from(dockSet).sort();

    const data = hourlyStats.map(([hour, stats]: [string, any]) => {
      const datum: any = { hour };
      docks.forEach(dock => {
        datum[dock] = stats.byDock[dock] || 0;
      });
      return datum;
    });

    return { data, docks };
  }, [hourlyStats]);

  async function startExportCSV() {
    const filename = generateExportFilename({
      exportTitle: t`simulation-job-timeline`,
      brandTitleState,
      warehouse,
      warehouseIdState,
      sim,
    });

    // Since this is static data from the schedule, we'll export the current data
    // In a real implementation, you might want to create a proper export job
    const exportData = getDatasetQueryDataRows(sortedData, converterConfig);

    // For now, return a mock export job - in a real implementation you'd call the backend
    return {
      errors: [],
      job: {
        id: 'mock-export-job',
        status: BatchJobStatus.READY,
        progress: 100,
        createdAt: new Date(),
        updatedAt: new Date(),
        sourceType: 'simulation-job-timeline',
        sourceId: 'mock',
        filename: `${filename}.csv`,
        expiresAt: new Date(Date.now() + 24 * 60 * 60 * 1000), // 24 hours from now
        downloadUrl: null,
        fileSize: null,
      } as ExportJobFragment,
    };
  }

  return (
    <>
      <TitleSection
        key="dock-chart"
        id="dock-chart"
        title="Dock Usage per Hour"
        inPanelView
        collapsible
      >
        <DockUsageChart
          data={chartData.data}
          docks={chartData.docks}
          hiddenDocks={hiddenDocks}
        />
      </TitleSection>

      <TitleSection
        key={`schedule-datatable-${''}`}
        id={`schedule-datatable-${''}`}
        title={`Timeline Data`}
        inPanelView
        collapsible
        classNameInner="h-full flex-1"
        customOpenIcon={
          <Icon.ChevronDualOpen className="hover:text-menu-active mx-1 h-4 w-4 fill-current" />
        }
        customClosedIcon={
          <Icon.CircleMenu className="hover:text-menu-active mx-1 h-4 w-4 fill-current" />
        }
      >
        <div
          data-component="SimulationJobTimelineDataTable"
          className={classNames(
            'flex h-full w-full flex-1 flex-col overflow-auto',
            'bg-app-panel-dark/60',
          )}
        >
          <DatasetTable
            id={'simulation-job-timeline-data-table'}
            hideScreenTitle
            isSticky
            isActionable
            columnsConfig={columnsConfig as any}
            keyFields={['job.id']}
            data={sortedData}
            onLoadNext={_.noop}
            onSearch={searchValues => {
              setTableState(prev => ({ ...prev, searchValues }));
            }}
            sortBy={tableState.sortValues}
            onSort={sortValues => {
              setTableState(prev => ({ ...prev, sortValues }));
            }}
            totalCount={totalCount}
            searchValues={tableState.searchValues}
            isLoading={false}
            onRowSelect={_.noop}
            onStartExportClick={startExportCSV}
            fieldsSummary={tableFieldsSummary}
            hasCounter
          />
        </div>
      </TitleSection>
    </>
  );
};

export default SimulationJobTimelineDataTable;
